import { Box, Tabs, Tab, Typography, Select, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const NAVY = '#052F5D';

/**
 * Renders the encounter tab navigator.
 *
 * Layout:
 *   - >= 900px (md): vertical rail on the left, with completion dots
 *     and a section count header.
 *   - < 900px:        compact top dropdown so tablets/phones don't
 *                     lose half their screen to nav.
 *
 * Tab completion is a *content* decision made by the caller and passed
 * in as `complete: boolean` per tab def.
 */
export default function TabRail({ tabs, activeIndex, onChange }) {
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));

    const completedCount = tabs.filter((t) => t.complete).length;

    if (isCompact) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    p: 1.25,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Section
                </Typography>
                <Select
                    fullWidth
                    size="small"
                    value={activeIndex}
                    onChange={(e) => onChange(Number(e.target.value))}
                    variant="outlined"
                    sx={{ fontWeight: 600, color: NAVY }}
                >
                    {tabs.map((t, i) => (
                        <MenuItem key={t.key} value={i}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {t.complete ? (
                                    <CheckCircleRoundedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                ) : (
                                    <FiberManualRecordIcon sx={{ fontSize: 8, color: 'text.disabled' }} />
                                )}
                                {t.label}
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
                <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {completedCount}/{tabs.length}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: 244,
                flexShrink: 0,
                bgcolor: '#fff',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                py: 1.5,
                pl: 0.5,
                pr: 0.25,
                alignSelf: 'flex-start',
                position: 'sticky',
                top: 16,
                boxShadow: '0 1px 2px rgba(5,47,93,0.04)',
            }}
        >
            <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}
                >
                    Encounter
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                    {completedCount}/{tabs.length}
                </Typography>
            </Box>
            <Tabs
                orientation="vertical"
                value={activeIndex}
                onChange={(_, v) => onChange(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                    sx: { left: 0, width: 3, borderRadius: '0 2px 2px 0', bgcolor: NAVY },
                }}
                sx={{
                    minHeight: 'auto',
                    '& .MuiTab-root': {
                        minHeight: 36,
                        py: 0.75,
                        px: 1.5,
                        textTransform: 'none',
                        fontSize: 13.5,
                        lineHeight: 1.2,
                        letterSpacing: 0,
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        color: 'text.secondary',
                        borderRadius: 1,
                        mr: 0.5,
                        transition: 'background-color 140ms ease, color 140ms ease',
                        '&:hover': { bgcolor: 'rgba(5,47,93,0.04)', color: NAVY },
                        '&.Mui-selected': {
                            color: NAVY,
                            fontWeight: 600,
                            bgcolor: 'rgba(5,47,93,0.06)',
                        },
                    },
                }}
            >
                {tabs.map((t) => (
                    <Tab
                        key={t.key}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Box
                                    sx={{
                                        width: 14,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    {t.complete ? (
                                        <CheckCircleRoundedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    ) : (
                                        <FiberManualRecordIcon sx={{ fontSize: 6, color: 'text.disabled' }} />
                                    )}
                                </Box>
                                <Box component="span" sx={{ whiteSpace: 'normal' }}>{t.label}</Box>
                            </Box>
                        }
                    />
                ))}
            </Tabs>
        </Box>
    );
}
